<div class="d-flex flex-column gap-3" data-testid="subscription-panel">
  <div class="d-flex flex-wrap align-items-start justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h3 class="av-section-title m-0">{{ 'backoffice.organizations.subscription_panel.title' | translate }}</h3>
      <p class="av-meta m-0">{{ 'backoffice.organizations.subscription_panel.control_note' | translate }}</p>
    </div>

    @if (hasSubscription()) {
      <a
        class="av-btn text-decoration-none"
        [routerLink]="['/organizations', organizationId(), 'subscription']"
        data-testid="subscription-control-link"
      >
        <mat-icon aria-hidden="true">tune</mat-icon>
        <span>{{ 'backoffice.organizations.subscription_panel.control_link' | translate }}</span>
      </a>
    }
  </div>

  @switch (facade.subscriptionState()) {
    @case ('error') {
      <av-empty-state
        icon="receipt_long"
        [title]="'backoffice.organizations.subscription_panel.error_title' | translate"
        [message]="'backoffice.organizations.subscription_panel.error_message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      @if (facade.subscription(); as subscription) {
        <dl class="detail-grid m-0">
          <dt class="av-meta">{{ 'backoffice.organizations.subscription_panel.plan' | translate }}</dt>
          <dd data-testid="subscription-plan">{{ subscription.planLabel }}</dd>

          <dt class="av-meta">{{ 'backoffice.organizations.subscription_panel.status' | translate }}</dt>
          <dd>
            <av-status-chip
              [label]="subscription.statusLabelKey | translate"
              [tone]="subscription.statusTone"
            ></av-status-chip>
          </dd>

          <dt class="av-meta">{{ 'backoffice.organizations.subscription_panel.seat_offer' | translate }}</dt>
          <dd>{{ subscription.seat_offer_type || unresolved }}</dd>

          <dt class="av-meta">{{ 'backoffice.organizations.subscription_panel.started' | translate }}</dt>
          <dd>
            {{ subscription.started_at ? (subscription.started_at | date: dateTimeFormat) : unresolved }}
          </dd>

          <dt class="av-meta">{{ 'backoffice.organizations.subscription_panel.current_period' | translate }}</dt>
          <dd data-testid="subscription-period">
            {{
              subscription.current_period_start
                ? (subscription.current_period_start | date: dateTimeFormat)
                : unresolved
            }}
            —
            {{
              subscription.current_period_end
                ? (subscription.current_period_end | date: dateTimeFormat)
                : unresolved
            }}
          </dd>

          <dt class="av-meta">{{ 'backoffice.organizations.subscription_panel.trial_ends' | translate }}</dt>
          <dd>
            {{
              subscription.trial_ends_at
                ? (subscription.trial_ends_at | date: dateTimeFormat)
                : unresolved
            }}
          </dd>

          <dt class="av-meta">{{ 'backoffice.organizations.subscription_panel.stripe_subscription' | translate }}</dt>
          <dd class="av-source">{{ subscription.stripe_subscription_id || unresolved }}</dd>

          <dt class="av-meta">{{ 'backoffice.organizations.subscription_panel.last_stripe_event' | translate }}</dt>
          <dd>
            {{
              subscription.last_stripe_event_at
                ? (subscription.last_stripe_event_at | date: dateTimeFormat)
                : unresolved
            }}
          </dd>
        </dl>

        @if (subscription.isTrialing) {
          <p class="av-section-hint m-0" data-testid="subscription-trial-note">{{ 'backoffice.organizations.subscription_panel.trial_note' | translate }}</p>
        }

        @if (subscription.isEndingAtPeriodEnd) {
          <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="subscription-cancel-note">
            <mat-icon aria-hidden="true">event_busy</mat-icon>
            <span>{{ 'backoffice.organizations.subscription_panel.cancel_note' | translate }}</span>
          </div>
        }

        @if (subscription.commitment.is_committed) {
          <p class="av-section-hint m-0" data-testid="subscription-commitment">
            {{ 'backoffice.organizations.subscription_panel.commitment_note' | translate }}
          </p>
        }

        @if (subscription.stripe_customer_owned) {
          <p class="av-meta m-0" data-testid="subscription-ownership">{{ 'backoffice.organizations.subscription_panel.owned_note' | translate }}</p>
        } @else {
          <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="subscription-ownership">
            <mat-icon aria-hidden="true">error_outline</mat-icon>
            <span>{{ 'backoffice.organizations.subscription_panel.foreign_note' | translate }}</span>
          </div>
        }

        <section class="d-flex flex-column gap-2">
          <h4 class="av-section-title m-0">{{ 'backoffice.organizations.subscription_panel.items_title' | translate }}</h4>
          <p class="av-meta m-0">{{ 'backoffice.organizations.subscription_panel.quantity_note' | translate }}</p>

          <div class="table-scroll">
            <table class="av-table w-100" data-testid="subscription-items">
              <thead>
                <tr>
                  <th scope="col">{{ 'backoffice.organizations.subscription_panel.table.component' | translate }}</th>
                  <th scope="col">{{ 'backoffice.organizations.subscription_panel.table.stripe_product' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.organizations.subscription_panel.table.stripe_quantity' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.organizations.subscription_panel.table.licence_rows' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.organizations.subscription_panel.table.unit_amount' | translate }}</th>
                  <th scope="col">{{ 'backoffice.organizations.subscription_panel.table.stripe_price' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (item of subscription.items; track item.id) {
                  <tr>
                    <td class="cell-strong">{{ componentLabelKey(item.component_type) | translate }}</td>
                    <td class="cell-muted">{{ productName(item.component_type) | translate }}</td>
                    <td class="text-end">{{ item.quantity }}</td>
                    <td class="text-end">
                      {{ item.licence_count }}
                      @if (isMismatched(item.quantity, item.licence_count)) {
                        <mat-icon class="warn-icon" [matTooltip]="'backoffice.organizations.subscription_panel.mismatch_note' | translate" aria-hidden="true">
                          warning
                        </mat-icon>
                      }
                    </td>
                    <td class="text-end">
                      {{ item.unit_amount | number: '1.2-2' }} {{ item.currency || unresolved }}
                    </td>
                    <td class="av-source">{{ item.stripe_price_id || unresolved }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      } @else {
        <av-empty-state
          icon="receipt_long"
          [title]="'backoffice.organizations.subscription_panel.empty_title' | translate"
          [message]="'backoffice.organizations.subscription_panel.empty_message' | translate"
        ></av-empty-state>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.organizations.subscription_panel.loading_title' | translate"
        [message]="'backoffice.organizations.subscription_panel.loading_message' | translate"
      ></av-empty-state>
    }
  }
</div>
